import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import dayjs from "dayjs";
import { getInLanguageOrDefault } from "../../common/utilities/language";
import { ActionType, HistoryItem, HistoryItemType } from "../interfaces/drawer";
import { NormalizationErrorType } from "../../annotation/interfaces/annotation";

export const useAnnotationHistoryContent = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation("annotationSideControls", {
    keyPrefix: "sideDrawer.annotationHistory",
  });

  const getDate = useCallback((item: HistoryItem) => {
    if (!item.createdDate) {
      return;
    }

    return dayjs(item.createdDate).format("DD/MM/YYYY HH:mm");
  }, []);

  const getTitle = useCallback(
    (item: HistoryItem) => {
      let actionKey;
      if (item.actionType === ActionType.ADD) {
        actionKey = "added";
      } else if (item.actionType === ActionType.MODIFIED) {
        actionKey = "modified";
      } else if (item.actionType === ActionType.DELETE) {
        actionKey = "deleted";
      }
      let typeKey;
      if (item.type === HistoryItemType.ENTITY) {
        typeKey = "entity";
      } else if (item.type === HistoryItemType.CATEGORIZATION) {
        typeKey = "categorization";
      } else if (item.type === HistoryItemType.DOCUMENT_TYPE) {
        typeKey = "documentType";
      }

      if (!typeKey || !actionKey) {
        return "";
      }

      return t(`itemTitle.${typeKey}.${actionKey}`);
    },
    [t],
  );

  const getIsError = useCallback((item: HistoryItem) => {
    return (
      item.errorType === NormalizationErrorType.INVALID_VALUE ||
      item.errorType === NormalizationErrorType.ERROR_WHILE_NORMALIZING
    );
  }, []);

  const getEntityName = useCallback(
    (item: HistoryItem) => {
      let entityString = "";
      if (item.itemTranslations) {
        // `HistoryItem` declares these as `Translation`, but they are parsed
        // here, so the API evidently sends them as JSON strings. The double
        // cast preserves the existing runtime behaviour without asserting
        // which of the two is the correct shape.
        entityString = getInLanguageOrDefault(
          JSON.parse(item.itemTranslations as unknown as string),
          language,
        );
      }

      if (item.groupBlockTranslations) {
        let groupBlockString = getInLanguageOrDefault(
          JSON.parse(item.groupBlockTranslations as unknown as string),
          language,
        );
        if (item.multipleGroupBlocks) {
          groupBlockString += ` #${item.index}`;
        }

        return `${entityString} [${groupBlockString}]`;
      }

      return entityString;
    },
    [language],
  );

  return {
    getDate,
    getTitle,
    getEntityName,
    getIsError,
    t,
  };
};
